import classnames from 'classnames';
import { forwardRef } from 'react';
import Dropzone from 'react-dropzone';
import { UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  NormalButton,
  ConfirmModal,
  TextInput,
  OverlayLoading,
  IcCopyReport as icCopyReport,
  IcDownloadReport as icDownloadReport,
  IcUploadReport as icUploadReport,
  ReloadIconPrimaryDark as reloadIcon,
} from '@/shared/ui';
import {
  CANT_GEN_DISMISSED_REPORT,
  CANT_GEN_SENT_REPORT,
  CANT_UPLOAD_SENT_REPORT,
  IS_NOT_EDIT_REPORT_MESSAGE,
} from '@/shared/config';
import PDFViewer from './PDFViewer';
import PdfReportLoadingOverlay from './pdfReportLoadingOverlay/pdfReportLoadingOverlay';
import ReportSettings from './ReportSettings';
import useAiPDFReportTab from './useAiPDFReportTab';
import './styles.scss';

export type AiPDFReportTabProps = {
  activeTab: string;
  isAbortedStudy?: boolean;
  isNotEdit?: boolean;
  isGeneratingNormalReportInCallCenter?: boolean;
};

export type AiPDFReportTabRef = {
  isChangedData: () => boolean;
  onClickSaveReport: () => void;
  resetData: () => void;
};

/**
 * The PDF report tab, pairing the rendered report preview with the facility notes, findings and report settings that feed it. useAiPDFReportTab supplies every value and handler; the tab renders confirm modals for copying to Call Center, overwriting findings and retrying a timed-out generation, and an action bar whose download, upload, copy and generate controls are disabled and tooltipped according to read-only mode and interim-report status.
 *
 * @param props - Tab props carrying read-only mode and whether Call Center is already generating a normal report.
 * @param ref - Ref passed to the hook so the parent can drive the tab's report actions.
 * @returns The rendered PDF report tab with its viewer, options column, action bar and confirmation modals.
 */
const AiPDFReportTab = forwardRef<AiPDFReportTabRef, AiPDFReportTabProps>(
  ({ activeTab, isAbortedStudy, isNotEdit, isGeneratingNormalReportInCallCenter }, ref) => {
    const {
      reportData,
      isShowTabHeader,
      facilityNote,
      findings,
      state,
      setState,
      onChange,
      isFacilityNoteDisabled,
      isTabActive,
      isFailedDownloadReport,
      dropzoneRef,
      reportSettingRef,
      handleGenerateReport,
      handleGenerateReportWhenError,
      toggleUpdateFindingsModal,
      hanldeCloseTimeoutReportModal,
      handleUpdateFinding,
      handleCopyReport,
      handleClickDownloadPdfReport,
      handleOnDropFile,
      isInterimReport,
      isDismissedInterimReport,
      isSentInterimReport,
      isDisabledDownloadFile,
      isDisabledCopyReport,
      isDisabledGenerateDefault,
      isDisabledGenerateReport,
      reportStatus,
    } = useAiPDFReportTab(
      { activeTab, isAbortedStudy, isNotEdit, isGeneratingNormalReportInCallCenter },
      ref,
    );

    return (
      <>
        <OverlayLoading isVisible={!isTabActive} />

        <ConfirmModal
          isOpen={state.isOpenCopyReportModal}
          onClickLeftButton={() => {
            setState({ isOpenCopyReportModal: false });
          }}
          leftButtonName={state.isSaving ? null : 'Cancel'}
          leftButtonId="copy-report-modal-cancel-btn"
          onClickRightButton={() => handleCopyReport(isInterimReport)}
          rightButtonName="Continue"
          rightButtonId="copy-report-modal-continue-btn"
          title="Copy report"
          isRedBtn={false}
          question="The current report in Call Center Portal will be overwritten with this report. Are you sure you want to continue?"
          isSaving={state.isSaving}
        />
        <ConfirmModal
          className="small-modal"
          isOpen={state.isOpenUpdateFindingsModal}
          onClickLeftButton={toggleUpdateFindingsModal}
          leftButtonName="Cancel"
          leftButtonId="update-findings-modal-cancel-btn"
          onClickRightButton={handleUpdateFinding}
          rightButtonName="Continue"
          rightButtonId="update-findings-modal-continue-btn"
          title="Update findings"
          isRedBtn={false}
          question="The current findings will be overwritten. Are you sure you want to continue?"
        />
        <ConfirmModal
          isOpen={state.isOpenTimeoutReportModal && !reportData?.report?.isArtifactReport}
          onClickLeftButton={hanldeCloseTimeoutReportModal}
          leftButtonName={state.isSaving ? null : 'Close'}
          leftButtonId="timeout-report-modal-close-btn"
          onClickRightButton={handleGenerateReportWhenError}
          rightButtonName="Generate"
          rightButtonId="timeout-report-modal-generate-btn"
          title="We ran into a problem"
          isRedBtn={false}
          question="Something went wrong while generating the report. Please try again."
          isSaving={state.isSaving}
          disabled={isDisabledGenerateDefault}
        />

        <div className={classnames('pdf-report-tab', isShowTabHeader ? '' : '--hide-tab')}>
          <PdfReportLoadingOverlay />
          <div className="pdf-content">
            <PDFViewer
              isRendering={reportStatus === 'Rendering'}
              isLoading={state.isDownloadingPdfReport}
              url={state.pdfReport?.objectUrl}
              reportStatus={reportStatus}
              isArtifactReport={reportData?.report?.isArtifactReport}
              handleButtonClick={handleGenerateReport}
              isDisabledGenerateReport={isDisabledGenerateReport}
              isFailedDownloadReport={isFailedDownloadReport}
            />
          </div>
          <div className="right-content">
            <div id="report-options" className="report-options">
              <div className="report-options__top-container">
                <div className="facility-notes">
                  <TextInput
                    className="input-list-item-textarea"
                    name="facilityNote"
                    type="textarea"
                    label="Facility notes"
                    value={facilityNote as any} // pre-existing bug: facilityNoteState is typed atom<String>, TextInput's value is string|number
                    onChange={onChange}
                    placeholder="Notes..."
                    id="facilityNote-input-id"
                    disabled={isFacilityNoteDisabled}
                  />
                  {isFacilityNoteDisabled && (
                    <UncontrolledTooltip
                      target="facilityNote-input-id"
                      placement="top"
                      delay={{ show: 0, hide: 0 }}
                      className="custom-tooltip"
                      hideArrow
                    >
                      Unable to edit facility notes
                    </UncontrolledTooltip>
                  )}
                </div>
                <div className="conclusion-holter-report">
                  <TextInput
                    className="input-list-item-textarea"
                    name="findings"
                    type="textarea"
                    label={
                      (
                        // pre-existing bug: TextInput's label is typed string, this passes a JSX element
                        <div className="findings">
                          <div className="title">Findings</div>
                          <IconButton
                            id="updateFindings"
                            isFilled
                            iconComponent={<img src={reloadIcon} alt="Reload icon" />}
                            onClick={toggleUpdateFindingsModal}
                          />
                          <UncontrolledTooltip
                            className="custom-tooltip"
                            target="updateFindings"
                            placement="bottom"
                            delay={{ show: 0, hide: 0 }}
                            hideArrow
                          >
                            Update findings
                          </UncontrolledTooltip>
                        </div>
                      ) as any
                    }
                    value={findings as any} // pre-existing bug: findingsState is typed atom<String>, TextInput's value is string|number
                    onChange={onChange}
                    placeholder="Comments..."
                    id="findings-input-id"
                  />
                </div>
              </div>
              <ReportSettings ref={reportSettingRef} />
            </div>
            <div id="report-actions" className="generate-pdf">
              <div id="ic-download-report">
                <IconButton
                  id="download-report-btn"
                  className="me-3"
                  isOutline
                  iconComponent={<img src={icDownloadReport} alt="icon" />}
                  onClick={() => {
                    handleClickDownloadPdfReport();
                  }}
                  disabled={isDisabledDownloadFile}
                />
              </div>
              <UncontrolledTooltip
                target="ic-download-report"
                className="custom-tooltip"
                placement="bottom-end"
                delay={{ show: 0, hide: 0 }}
                hideArrow
              >
                Download report
              </UncontrolledTooltip>
              <Dropzone
                ref={dropzoneRef}
                disableClick
                // react-dropzone's InputHTMLAttributes typing doesn't include data-* keys
                inputProps={{ 'data-testid': 'upload-report-input' } as any}
                className="upload-report-dropzone"
                onDrop={(files) => {
                  handleOnDropFile(files);
                }}
                accept="application/pdf"
                multiple={false}
                disabled={isDisabledDownloadFile}
              >
                <div id="ic-upload-report">
                  <IconButton
                    id="upload-report-btn"
                    className="me-3"
                    isOutline
                    iconComponent={<img src={icUploadReport} alt="icon" />}
                    onClick={() => {
                      dropzoneRef.current.open();
                    }}
                    disabled={isDisabledDownloadFile || isSentInterimReport}
                  />
                </div>
                <UncontrolledTooltip
                  target="ic-upload-report"
                  className="custom-tooltip"
                  placement="bottom"
                  delay={{ show: 0, hide: 0 }}
                  hideArrow
                >
                  {isSentInterimReport ? CANT_UPLOAD_SENT_REPORT : 'Upload report'}
                </UncontrolledTooltip>
              </Dropzone>
              <div id="copyReportBtn">
                <IconButton
                  id="copy-report-btn"
                  className="me-3"
                  isOutline
                  iconComponent={<img src={icCopyReport} alt="icon" />}
                  onClick={() => {
                    setState({ isOpenCopyReportModal: true });
                  }}
                  disabled={isDisabledCopyReport || isGeneratingNormalReportInCallCenter}
                />
              </div>

              <UncontrolledTooltip
                target="copyReportBtn"
                className="custom-tooltip"
                placement="bottom"
                delay={{ show: 0, hide: 0 }}
                hideArrow
              >
                {isNotEdit
                  ? IS_NOT_EDIT_REPORT_MESSAGE
                  : isGeneratingNormalReportInCallCenter
                    ? 'The report is being generated on Call Center'
                    : 'Copy report to Call Center'}
              </UncontrolledTooltip>
              <div id="generatePdfReportBtn">
                <NormalButton
                  id="generate-report-btn"
                  className="btn-generate-report"
                  buttonName="Generate Report"
                  onClick={() => {
                    handleGenerateReport(false);
                  }}
                  disabled={isDisabledGenerateReport}
                />
              </div>
              {(isNotEdit || isSentInterimReport || isDismissedInterimReport) && (
                <UncontrolledTooltip
                  target="generatePdfReportBtn"
                  className="custom-tooltip"
                  placement="bottom"
                  delay={{ show: 0, hide: 0 }}
                  hideArrow
                >
                  {isSentInterimReport
                    ? CANT_GEN_SENT_REPORT
                    : isDismissedInterimReport
                      ? CANT_GEN_DISMISSED_REPORT
                      : IS_NOT_EDIT_REPORT_MESSAGE}
                </UncontrolledTooltip>
              )}
            </div>
          </div>
        </div>
      </>
    );
  },
);

export default AiPDFReportTab;
